import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Speed, FileDownload } from '@mui/icons-material';
import { apiService } from '../../services/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SprintVelocityData {
  sprintId: string;
  sprintName: string;
  completedStoryPoints: number;
  velocity: number;
  duration: number;
  completedAt: Date;
}

interface ProjectVelocityData {
  sprints: SprintVelocityData[];
  averageVelocity: number;
  velocityTrend: 'increasing' | 'decreasing' | 'stable';
  totalSprints: number;
}

interface VelocityDashboardProps {
  projectId: string;
  height?: number;
  showControls?: boolean;
  onDataLoaded?: (data: ProjectVelocityData) => void;
}

const VelocityDashboard: React.FC<VelocityDashboardProps> = ({
  projectId,
  height = 400,
  showControls = true,
  onDataLoaded,
}) => {
  const [data, setData] = useState<ProjectVelocityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [limit, setLimit] = useState(6);

  const loadVelocityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<ProjectVelocityData>(
        `/projects/${projectId}/velocity?limit=${limit}`
      );

      if (response.success && response.data) {
        setData(response.data);
        onDataLoaded?.(response.data);
      } else {
        setError('Failed to load velocity data');
      }
    } catch (err: any) {
      console.error('Error loading velocity data:', err);
      setError(err.response?.data?.message || 'Failed to load velocity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadVelocityData();
    }
  }, [projectId, limit]);

  const getChartData = () => {
    if (!data || data.sprints.length === 0) return null;

    const sortedSprints = [...data.sprints].reverse(); // Show oldest to newest
    const labels = sortedSprints.map(sprint => sprint.sprintName);
    const velocities = sortedSprints.map(sprint => sprint.velocity);
    const completedPoints = sortedSprints.map(sprint => sprint.completedStoryPoints);

    if (chartType === 'line') {
      return {
        labels,
        datasets: [
          {
            label: 'Velocity (Points/Day)',
            data: velocities,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.1,
            yAxisID: 'y',
          },
          {
            label: 'Average Velocity',
            data: new Array(velocities.length).fill(data.averageVelocity),
            borderColor: 'rgb(156, 163, 175)',
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            borderDash: [5, 5],
            fill: false,
            tension: 0,
            yAxisID: 'y',
          },
        ],
      };
    } else {
      return {
        labels,
        datasets: [
          {
            label: 'Completed Story Points',
            data: completedPoints,
            backgroundColor: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgb(34, 197, 94)',
            borderWidth: 1,
            yAxisID: 'y1',
          },
          {
            label: 'Velocity (Points/Day)',
            data: velocities,
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1,
            yAxisID: 'y',
          },
        ],
      };
    }
  };

  const getChartOptions = () => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Team Velocity Tracking',
          font: {
            size: 16,
            weight: 'bold' as const,
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Sprints',
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          title: {
            display: true,
            text: 'Velocity (Points/Day)',
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false,
      },
    };

    if (chartType === 'bar') {
      return {
        ...baseOptions,
        scales: {
          ...baseOptions.scales,
          y1: {
            type: 'linear' as const,
            display: true,
            position: 'right' as const,
            title: {
              display: true,
              text: 'Completed Story Points',
            },
            beginAtZero: true,
            grid: {
              drawOnChartArea: false,
            },
          },
        },
      };
    }

    return baseOptions;
  };

  const getTrendIcon = () => {
    if (!data) return null;

    switch (data.velocityTrend) {
      case 'increasing':
        return <TrendingUp color="success" />;
      case 'decreasing':
        return <TrendingDown color="error" />;
      case 'stable':
        return <TrendingFlat color="info" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    if (!data) return 'default';

    switch (data.velocityTrend) {
      case 'increasing':
        return 'success';
      case 'decreasing':
        return 'error';
      case 'stable':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTrendText = () => {
    if (!data) return 'Unknown';

    switch (data.velocityTrend) {
      case 'increasing':
        return 'Improving';
      case 'decreasing':
        return 'Declining';
      case 'stable':
        return 'Stable';
      default:
        return 'Unknown';
    }
  };

  const calculateVelocityStats = () => {
    if (!data || data.sprints.length === 0) return null;

    const velocities = data.sprints.map(sprint => sprint.velocity);
    const minVelocity = Math.min(...velocities);
    const maxVelocity = Math.max(...velocities);
    const latestVelocity = velocities[velocities.length - 1];

    return {
      minVelocity,
      maxVelocity,
      latestVelocity,
      range: maxVelocity - minVelocity,
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ height }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.sprints.length === 0) {
    return (
      <Alert severity="info" sx={{ height }}>
        No velocity data available. Complete some sprints to see velocity trends.
      </Alert>
    );
  }

  const chartData = getChartData();
  const velocityStats = calculateVelocityStats();

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {showControls && (
          <Box mb={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    label="Chart Type"
                    onChange={(e) => setChartType(e.target.value as 'line' | 'bar')}
                  >
                    <MenuItem value="line">Line Chart</MenuItem>
                    <MenuItem value="bar">Bar Chart</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Sprint Limit</InputLabel>
                  <Select
                    value={limit}
                    label="Sprint Limit"
                    onChange={(e) => setLimit(Number(e.target.value))}
                  >
                    <MenuItem value={3}>Last 3 Sprints</MenuItem>
                    <MenuItem value={6}>Last 6 Sprints</MenuItem>
                    <MenuItem value={10}>Last 10 Sprints</MenuItem>
                    <MenuItem value={20}>Last 20 Sprints</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Speed color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Velocity Trend
                  </Typography>
                  <Chip
                    size="small"
                    color={getTrendColor() as any}
                    icon={getTrendIcon() || undefined}
                    label={getTrendText()}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" justifyContent="flex-end">
                  <Chip
                    icon={<FileDownload />}
                    label="Export CSV"
                    onClick={() => {
                      if (!data) return;
                      const rows = [
                        ['Sprint', 'Velocity (points/day)', 'Completed Points'] as any[],
                        ...data.sprints.map(s => [s.sprintName, s.velocity, s.completedStoryPoints])
                      ];
                      const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'velocity.csv';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    }}
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Velocity Stats Cards */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {data.averageVelocity.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Velocity
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {velocityStats?.latestVelocity.toFixed(1) || '0'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest Velocity
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="info.main">
                {velocityStats?.maxVelocity.toFixed(1) || '0'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Highest Velocity
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">
                {data.totalSprints}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Sprints
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Chart */}
        <Box sx={{ height: height - 300, position: 'relative' }}>
          {chartData && (
            <>
              {chartType === 'line' ? (
                <Line data={chartData} options={getChartOptions()} />
              ) : (
                <Bar data={chartData} options={getChartOptions()} />
              )}
            </>
          )}
        </Box>

        {/* Velocity Insights */}
        {velocityStats && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Velocity Insights
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Velocity Range:</strong> {velocityStats.minVelocity.toFixed(1)} - {velocityStats.maxVelocity.toFixed(1)} points/day
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Consistency:</strong> {velocityStats.range < data.averageVelocity * 0.3 ? 'High' : velocityStats.range < data.averageVelocity * 0.6 ? 'Medium' : 'Low'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Trend:</strong> {getTrendText()} velocity over last {data.sprints.length} sprints
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Latest vs Average:</strong> {velocityStats.latestVelocity > data.averageVelocity ? '+' : ''}{(velocityStats.latestVelocity - data.averageVelocity).toFixed(1)} points/day
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VelocityDashboard;
